import React from 'react';
import {
  WidgetErrorState,
  WidgetType,
} from '../../../../shared/constants/widget';
import { NoData } from '../../../widgets/WidgetStates';
import {
  InvalidFiltersError,
  MultipleOrganisationsSelectedError,
  NoDataError,
} from '../errors';
import WidgetError from './WidgetError';

type Props = React.PropsWithChildren<{
  widgetType?: WidgetType;
  resetKey?: string;
}>;

export default class WidgetErrorBoundary extends React.Component<
  Props,
  { error: Error | null; hasError: boolean }
> {
  constructor(props: Props) {
    super(props);
    this.state = { error: null, hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { error, hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Widget error:', error, errorInfo);
  }

  componentDidUpdate(prevProps: Props) {
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null, hasError: false });
    }
  }

  render() {
    const { error, hasError } = this.state;

    if (error instanceof NoDataError) {
      return (
        <NoData type={this.props.widgetType} errorType={error.errorType} />
      );
    }

    if (error instanceof InvalidFiltersError) {
      return (
        <WidgetError>
          <WidgetError.Label>
            Sorry, we don’t have data to display. Please remove filters to see
            data in this widget
          </WidgetError.Label>
        </WidgetError>
      );
    }

    if (error instanceof MultipleOrganisationsSelectedError) {
      return (
        <WidgetError>
          <WidgetError.Label>
            Please select only one organisation to view results
          </WidgetError.Label>
        </WidgetError>
      );
    }

    if (hasError) {
      return (
        <NoData
          type={this.props.widgetType}
          errorType={WidgetErrorState.API_FAIL}
        />
      );
    }

    return this.props.children;
  }
}
